/* eslint-disable camelcase */
import React from 'react';
import { MatomoProvider } from '@datapunt/matomo-tracker-react';
import { MatomoInstance } from '@datapunt/matomo-tracker-react/lib/types';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { MsalProvider } from '@azure/msal-react';
import AppErrorBoundary from './AppErrorBoundry';
import { setupStore } from './store';
import './index.scss';
import reportWebVitals from './reportWebVitals';
import CustomThemeProvider from './theme/CustomThemeProvider';
import * as serviceWorker from './serviceWorker';
import { analytics_service } from './services/analytics';
import { client } from './graph/apolloSetup';
import { MATOMO_URL, MATOMO_SITE_ID } from './constants';
import { App2 } from './hooks/app-tool';
import { getMsalInstance } from './msal-dynamic-config';
import { initializeAppInsights, reactPlugin } from './AppInsights';
import matomoConfig from './utils/matomoConfig';

const ReduxProvider = Provider as any;

if (matomoConfig.ANALYTICS.active) {
const _paq = (window._paq = window._paq || []);
_paq.push(['trackPageView']);
_paq.push(['enableLinkTracking']);
(function () {
  const u = MATOMO_URL;
  _paq.push(['setTrackerUrl', `${u}matomo.php`]);
  _paq.push(['setSiteId', MATOMO_SITE_ID]);
  const d = document;
  const g = d.createElement('script');
  const s = d.getElementsByTagName('script')[0];
  g.async = true;
  g.src = `${u}matomo.js`;
  s.parentNode.insertBefore(g, s);
})();
}

const instance = analytics_service.initializeAnalyticsClient();

interface MatomoProviderProps {
  value: MatomoInstance;
  children: React.ReactNode;
}

const CustomMatomoProvider: React.FC<MatomoProviderProps> = (props) => (
  <MatomoProvider {...props} />
);

// Async Initialization
const initializeApp = async () => {
  try {
    // Wait for MSAL to be initialized with dynamic config
    const msalInstance = await getMsalInstance();
    initializeAppInsights();

    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <AppInsightsContext.Provider value={reactPlugin}>
            <AppErrorBoundary>
              <CustomMatomoProvider value={instance}>
                <ReduxProvider store={setupStore()}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <CssBaseline enableColorScheme />
                    <StyledEngineProvider injectFirst>
                      <CustomThemeProvider>
                        <ApolloProvider client={client}>
                          <App2 />
                        </ApolloProvider>
                      </CustomThemeProvider>
                    </StyledEngineProvider>
                  </LocalizationProvider>
                </ReduxProvider>
              </CustomMatomoProvider>
            </AppErrorBoundary>
          </AppInsightsContext.Provider>
        </MsalProvider>
      </React.StrictMode>
    );

    serviceWorker.register();
    reportWebVitals();

  } catch (error) {
    console.error("Failed to initialize app:", error);
    // Optional: Render error state
  }
};

initializeApp();